import axios from "axios";
import {
  ComputeEstimateInput,
  EstimateBreakdown,
  MulchMap,
  SaveMulchMapInput,
  ServiceAvailability,
} from "./types";

/**
 * Get the current (isCurrent=true) mulch map for a location. Optional
 * mapGroupId scopes the lookup to a specific Map — used by the editor
 * to load a specific Map's current version. Without mapGroupId, returns
 * the most recently updated current at the location (alias behavior).
 * Returns null if nothing matches.
 */
export const getCurrentMulchMap = async (
  locationId: string,
  mapGroupId?: string,
): Promise<MulchMap | null> => {
  try {
    const res = await axios.get(`/mulch-maps/location/${locationId}`, {
      params: mapGroupId ? { mapGroupId } : {},
    });
    return res?.data || null;
  } catch (e: any) {
    // Session 23: previously returned e.response.data (a NestJS error
    // envelope like { statusCode, message }) as if it were a MulchMap,
    // which callers then dereferenced silently. Return null on error
    // and log so failures are visible in the browser console instead of
    // materializing as ghost "maps" with no id.
    // eslint-disable-next-line no-console
    console.error("getCurrentMulchMap failed", {
      locationId,
      mapGroupId,
      status: e?.response?.status,
      body: e?.response?.data,
      message: e?.message,
    });
    return null;
  }
};

/**
 * List versions for a location. Optional mapGroupId restricts to a single
 * Map's chain — the shape the version history modal wants in the
 * multi-variant world.
 */
export const getMulchMapVersions = async (
  locationId: string,
  mapGroupId?: string,
): Promise<MulchMap[]> => {
  try {
    const res = await axios.get(
      `/mulch-maps/location/${locationId}/versions`,
      { params: mapGroupId ? { mapGroupId } : {} },
    );
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/**
 * Service availability at a location: which services (and their providers)
 * are offered at these coordinates, and which add-ons per service.
 */
export const getServiceAvailability = async (
  locationId: string,
  productId?: string,
): Promise<ServiceAvailability | null> => {
  try {
    const res = await axios.get(
      `/mulch-maps/location/${locationId}/availability`,
      { params: productId ? { productId } : {} },
    );
    return res?.data || null;
  } catch (e: any) {
    // A failed availability lookup (e.g. a location with no coordinates yet,
    // which the service-area lookup can't resolve and returns 400) means
    // "availability unknown" — return null, not the error body. Returning the
    // error object made callers see a truthy value with no `services` array,
    // which crashed the editor's service-type render.
    return null;
  }
};

/**
 * Fetch a specific version of a mulch map by id.
 */
export const getMulchMap = async (id: string): Promise<MulchMap | null> => {
  try {
    const res = await axios.get(`/mulch-maps/${id}`);
    return res?.data || null;
  } catch {
    return null;
  }
};

/**
 * Save a new version. When `mapGroupId` is in the input, the save appends
 * to that Map's chain; otherwise a new mapGroupId is generated by the
 * backend and this row becomes v1 of a fresh Map at the location.
 */
export const saveMulchMap = async (
  input: SaveMulchMapInput,
): Promise<MulchMap | { error: string; status?: number; body?: any }> => {
  try {
    const res = await axios.post(`/mulch-maps`, input);
    return res?.data;
  } catch (e: any) {
    // Session 23: previously returned e.response.data as-is, which was
    // a NestJS error envelope without an `id` field. The hook's
    // `if ("id" in res)` check then bounced it as a generic failure,
    // with no visibility into WHY the backend rejected the save. Now
    // we normalize into { error, status, body } so useMulchMap can
    // surface the real backend message via toast.
    const status = e?.response?.status;
    const body = e?.response?.data;
    // eslint-disable-next-line no-console
    console.error("saveMulchMap failed", {
      status,
      body,
      message: e?.message,
      input: { locationId: input.locationId, mapGroupId: input.mapGroupId },
    });
    const message =
      body?.message ||
      (Array.isArray(body?.errors) ? body.errors.join(", ") : null) ||
      e?.message ||
      "Failed to save mulch map.";
    return { error: message, status, body };
  }
};

/**
 * Restore an old version by cloning it as a new current version WITHIN
 * the same mapGroup — the restore stays in its own Map's chain.
 */
export const restoreMulchMapVersion = async (
  mulchMapId: string,
): Promise<MulchMap | { error: string }> => {
  try {
    const res = await axios.post(`/mulch-maps/${mulchMapId}/restore`);
    return res?.data;
  } catch (e: any) {
    return e?.response?.data
      ? e.response.data
      : { error: "Failed to restore version." };
  }
};

/**
 * Compute cost / retail / margin for a given set of zones + service selection.
 * Uses the same pricing engine as RFQ creation.
 */
export const computeMulchEstimate = async (
  input: ComputeEstimateInput,
): Promise<EstimateBreakdown | { error: string }> => {
  try {
    const res = await axios.post(`/mulch-maps/estimate`, input);
    return res?.data;
  } catch (e: any) {
    // Surface a readable reason (the MapEditor now shows this instead of a
    // blank price). Prefer the backend message; a bare 400 with no useful
    // message most often means no provider offers this product + service at
    // this location, so say that rather than "Bad Request".
    const body = e?.response?.data;
    const raw =
      body?.message ||
      (Array.isArray(body?.errors) ? body.errors.join(", ") : null) ||
      body?.error ||
      null;
    const message =
      raw && String(raw).toLowerCase() !== "bad request"
        ? raw
        : "No provider offers this product and service at this location. Check provider coverage for the site.";
    return { error: message };
  }
};

/**
 * Soft-delete a specific version (rare in Phase 1).
 */
export const deleteMulchMap = async (
  id: string,
): Promise<{ success: boolean }> => {
  try {
    const res = await axios.delete(`/mulch-maps/${id}`);
    return res?.data || { success: false };
  } catch {
    return { success: false };
  }
};

/**
 * Archive a whole Map (mapGroup) — soft-deletes every version in the chain.
 * Used by the Location detail page's per-Map Archive action.
 */
export const archiveMapGroup = async (
  mapGroupId: string,
): Promise<{ success: boolean; archived?: number; error?: string }> => {
  try {
    const res = await axios.post(
      `/mulch-maps/group/${mapGroupId}/archive`,
    );
    return res?.data || { success: false };
  } catch (e: any) {
    return e?.response?.data
      ? e.response.data
      : { success: false, error: "Failed to archive map." };
  }
};

/**
 * Restore an archived Map (mapGroup). Complements archiveMapGroup.
 */
export const restoreMapGroup = async (
  mapGroupId: string,
): Promise<{ success: boolean; restored?: number; error?: string }> => {
  try {
    const res = await axios.post(
      `/mulch-maps/group/${mapGroupId}/restore`,
    );
    return res?.data || { success: false };
  } catch (e: any) {
    return e?.response?.data
      ? e.response.data
      : { success: false, error: "Failed to restore map." };
  }
};

/**
 * Session 20 Commit 4 — attach (or detach with mulchMapId=null) a specific
 * Map version to a downstream record. Backend enforces company scoping on
 * both the record and the Map.
 *
 * Phase B: `skipProfileWarning` short-circuits the cross-profile confirmation
 * gate. First call omits the flag; if the Map's profile and the record's
 * profile differ, the backend returns
 * `{ success: false, requiresConfirmation: true, warning }`. The frontend
 * then shows the warning and calls again with `skipProfileWarning: true`
 * to force the attach.
 */
export interface AttachMapWarning {
  code: "cross_profile";
  message: string;
  mapProfileId: string;
  recordProfileId: string;
  mapClientName: string | null;
  recordClientName: string | null;
}

export type AttachMapResult =
  | {
      success: true;
      // Session 25: newly-attached mulchMapId echoed back so the
      // frontend can update local state without a full refetch.
      mulchMapId?: string | null;
      // Session 25 diagnostic: what a same-connection SELECT reads
      // back for mulchMapId immediately after the UPDATE. Diverges
      // from mulchMapId only when the write silently failed to
      // persist.
      postWriteMulchMapId?: string | null;
      warning?: AttachMapWarning;
      mapInherited?: true;
      error?: undefined;
    }
  | { success: false; requiresConfirmation: true; warning: AttachMapWarning; error?: undefined }
  | { success: false; error: string };

export const attachMapToRecord = async (input: {
  recordType: "quote_request" | "quote" | "project" | "invoice" | "purchase_order";
  recordId: string;
  mulchMapId: string | null;
  skipProfileWarning?: boolean;
}): Promise<AttachMapResult> => {
  try {
    const res = await axios.post(`/mulch-maps/attach`, input);
    return res?.data || { success: false, error: "Attach returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? e.response.data
      : { success: false, error: "Failed to attach map." };
  }
};

/**
 * Session 20 Commit 6 — fetch a single Map's export payload as the
 * getmulch.map JSON envelope (round-trippable via importPayload below).
 */
export const exportMulchMap = async (
  mulchMapId: string,
): Promise<any | null> => {
  try {
    const res = await axios.get(`/mulch-maps/${mulchMapId}/export`);
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * Session 20 Commit 6 — bundle-export every Map at a Location as a single
 * getmulch.map.bundle JSON envelope.
 */
export const exportLocationMulchMaps = async (
  locationId: string,
): Promise<any | null> => {
  try {
    const res = await axios.get(
      `/locations/${locationId}/mulch-maps/export`,
    );
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * Session 20 Commit 7 — import a getmulch.map or getmulch.map.bundle
 * payload as new Map(s) at the payload's coordinates. Returns the target
 * Location's id + the mapGroupIds that were created so the caller can
 * navigate to the imported map(s).
 *
 * Phase B §6.5: `unmatchedClientNames` lists any clientName values from
 * the payload that couldn't be matched to a Client in this company. UI
 * surfaces this as a soft banner — imports still succeed, just without
 * the profile assignment for those maps.
 */
export const importMulchMap = async (
  payload: any,
  entryChoices?: Record<
    number,
    { choice: "new" | "overwrite" | "skip"; targetMapGroupId?: string }
  >,
): Promise<
  | {
      locationId: string;
      mapGroupIds: string[];
      unmatchedClientNames?: string[];
      overwrittenMapGroupIds?: string[];
      skippedCount?: number;
    }
  | { error: string }
> => {
  try {
    // Session 24: entryChoices rides on the same POST body, sibling to
    // the payload's own top-level fields. Backend controller extracts it
    // before passing the rest as payload.
    const body = entryChoices ? { ...payload, entryChoices } : payload;
    const res = await axios.post(`/mulch-maps/import`, body);
    return res?.data ?? { error: "Import returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Import failed." }
      : { error: "Import failed." };
  }
};

/**
 * Session 24: preview the import without committing. Returns per-entry
 * duplicate detection so the UI can prompt for New/Overwrite/Skip on
 * each entry that would collide with an existing Map at the target site.
 *
 * If the target Location doesn't exist yet at the payload's coords, no
 * duplicate is possible — the response comes back with locationExisted=false
 * and every entry has duplicateOf=null. In that case the caller can go
 * straight to importMulchMap without a modal.
 */
export interface ImportPreviewEntry {
  index: number;
  name: string;
  clientName: string | null;
  resolvedClientId: string | null;
  clientResolved: boolean;
  duplicateOf: null | {
    mapGroupId: string;
    currentMapId: string;
    name: string | null;
    version: number;
    clientName: string | null;
    updatedAt: string;
  };
}

export interface ImportPreviewResult {
  locationId: string | null;
  locationExisted: boolean;
  entries: ImportPreviewEntry[];
}

export const previewImportMulchMap = async (
  payload: any,
): Promise<ImportPreviewResult | { error: string }> => {
  try {
    const res = await axios.post(`/mulch-maps/import/preview`, payload);
    return res?.data ?? { error: "Preview returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Preview failed." }
      : { error: "Preview failed." };
  }
};

// ---------------------------------------------------------------------------
// Snapshots (Session 24 addendum — Google Static Maps API path)
// ---------------------------------------------------------------------------
//
// Snapshots pin to a specific map VERSION (mulch_map row id), not the
// mapGroup. Backend fetches the Static Maps PNG server-side and uploads
// to S3; frontend gets the signed URL on demand.

export interface MulchMapSnapshotSummary {
  id: string;
  imageUrl: string | null; // S3 key — not directly renderable
  takenAt: string;
  /**
   * Session 28 hotfix 3: which basemap was captured. Values match the
   * frontend BasemapId union: 'google_hybrid' | 'mapbox_sat' | 'esri_sat'
   * | 'google_3d'. Null for snapshots taken before the migration ran.
   */
  basemap?: string | null;
}

export const generateMapSnapshot = async (
  mulchMapId: string,
  viewState: {
    centerLat: number;
    centerLng: number;
    zoom: number;
    width?: number;
    height?: number;
    maptype?: "satellite" | "hybrid" | "roadmap" | "terrain";
  },
): Promise<MulchMapSnapshotSummary | { error: string }> => {
  try {
    const res = await axios.post(
      `/mulch-maps/${mulchMapId}/snapshots`,
      viewState,
    );
    return res?.data ?? { error: "Snapshot returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Snapshot generation failed." }
      : { error: "Snapshot generation failed." };
  }
};

/**
 * Session 28: client-side capture upload. Frontend renders the map to
 * a PNG Blob (see utils/captureMap.ts) and POSTs it here as multipart.
 * viewState is metadata only — logged server-side, not persisted today.
 */
export const uploadMapSnapshot = async (
  mulchMapId: string,
  blob: Blob,
  viewState: {
    centerLat: number;
    centerLng: number;
    zoom: number;
    tilt?: number;
    heading?: number;
    basemap?: string;
  },
): Promise<MulchMapSnapshotSummary | { error: string }> => {
  try {
    const form = new FormData();
    form.append("file", blob, "snapshot.png");
    form.append("view", JSON.stringify(viewState));
    const res = await axios.post(
      "/mulch-maps/" + mulchMapId + "/snapshots/upload",
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res?.data ?? { error: "Snapshot upload returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Snapshot upload failed." }
      : { error: "Snapshot upload failed." };
  }
};

/**
 * Session 28 hotfix 11: fetch metadata + signed URLs for the selected
 * snapshots on a Quote / Invoice / PO. Access-checked backend endpoint
 * that returns everything the SnapshotSelectionDisplay component needs
 * in one roundtrip. Works for company users, clients, and providers
 * (each with their own access check server-side).
 */
export const getRecordSelectedSnapshots = async (
  recordType: "quote" | "invoice" | "purchase_order",
  recordId: string,
): Promise<
  | Array<{
      id: string;
      basemap: string | null;
      takenAt: string;
      signedUrl: string | null;
    }>
  | { error: string }
> => {
  try {
    const res = await axios.get(
      "/mulch-maps/records/" + recordType + "/" + recordId + "/selected-snapshots",
    );
    return Array.isArray(res?.data) ? res.data : [];
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Failed to load site maps." }
      : { error: "Failed to load site maps." };
  }
};

/**
 * Session 28 hotfix 6 (Phase 1): persist an ordered list of snapshot
 * IDs on a Quote / Invoice / PO. The picker on the record view calls
 * this on popover-close; PDF gen reads the persisted list.
 */
export const setRecordSnapshotSelection = async (
  recordType: "quote" | "invoice" | "purchase_order",
  recordId: string,
  snapshotIds: string[],
): Promise<{ savedIds: string[]; prunedIds: string[] } | { error: string }> => {
  try {
    const res = await axios.post(
      "/mulch-maps/records/snapshot-selection",
      { recordType, recordId, snapshotIds },
    );
    return res?.data ?? { error: "Snapshot selection save returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Snapshot selection save failed." }
      : { error: "Snapshot selection save failed." };
  }
};

export const getMapSnapshots = async (
  mulchMapId: string,
): Promise<MulchMapSnapshotSummary[]> => {
  try {
    const res = await axios.get(`/mulch-maps/${mulchMapId}/snapshots`);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

export const getSnapshotSignedUrl = async (
  snapshotId: string,
): Promise<string | null> => {
  try {
    const res = await axios.get(`/mulch-maps/snapshots/${snapshotId}/url`);
    return res?.data?.url ?? null;
  } catch {
    return null;
  }
};

export const deleteMapSnapshot = async (
  snapshotId: string,
): Promise<{ success: boolean } | { error: string }> => {
  try {
    const res = await axios.delete(`/mulch-maps/snapshots/${snapshotId}`);
    return res?.data ?? { success: true };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Snapshot delete failed." }
      : { error: "Snapshot delete failed." };
  }
};

export * from "./types";
